import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { map, takeUntil } from 'rxjs/operators';

import { CartTipTypeValues } from '@config/values/cart.values';
import { MarketplaceKeys } from '@config/keys/shop.keys';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import { tipAmountFromPercent } from '@shared/utils/currency/round-tip-amount.util';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import { ModalsService } from '@core/services/modals/modals.service';
import { CartTipPickerModalComponent } from '@shared/modals/cart-tip-picker-modal/cart-tip-picker-modal.component';

@Component({
  selector: 'app-cart-tip-atom',
  templateUrl: './cart-tip-atom.component.html',
  standalone: false,
})
export class CartTipAtomComponent extends DestroyerBase implements OnInit, OnChanges {
  @Input() choice1Amount!: number;
  @Input() choice1Unit!: string;
  @Input() choice2Amount!: number;
  @Input() choice2Unit!: string;
  @Input() choice3Amount!: number;
  @Input() choice3Unit!: string;
  @Input() totalAmount!: number;
  @Input() isOtherAmountModal = true;
  @Input() selectedTip = 0;
  @Output() tipEmitter = new EventEmitter<number>();
  @Output() otherAmountEmitter = new EventEmitter<boolean>();

  isChoice1Selected = false;
  isChoice2Selected = false;
  isChoice3Selected = false;
  isOtherAmountSelected = false;

  otherAmount = 0;

  tipTypeValues = CartTipTypeValues;

  /** CHF: percent tips rounded to 5 Rp.; synced with marketplace load. */
  marketplaceCurrency: string | undefined;

  constructor(
    private orderSelector: OrderStoreSelector,
    private readonly modalsService: ModalsService,
    private readonly marketplaceSelector: MarketplaceStoreSelector,
  ) {
    super();
  }

  ngOnInit(): void {
    this.marketplaceSelector.selectMarketplace$.pipe(takeUntil(this._destroyerRef)).subscribe(m => {
      this.marketplaceCurrency = m?.[MarketplaceKeys.Currency];
    });

    this.orderSelector.selectValidation$
      .pipe(
        takeUntil(this._destroyerRef),
        map(validation => validation?.tip?.amount),
      )
      .subscribe(tip => {
        if (tip) {
          this.selectedTip = tip / 100;
          this.checkOtherAmountIsSelected();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedTip) {
      this.checkOtherAmountIsSelected();
    }
  }

  onSelectTip(tip: any): void {
    this.selectedTip = tip;
    this.isOtherAmountSelected = false;
    this.otherAmount = 0;
    this.tipEmitter.emit(tip * 100);
  }

  selectOtherTipAmount(): void {
    if (!this.isOtherAmountModal) {
      this.otherAmountEmitter.emit(true);
      return;
    }
    this.modalsService.open(CartTipPickerModalComponent.handle);
  }

  calculateAmount(value: number, totalAmount: number): number {
    return tipAmountFromPercent(totalAmount, value, this.marketplaceCurrency);
  }

  getChoiceSecondaryAmount(choiceAmount: number, choiceUnit: string): number | undefined {
    if (choiceUnit !== CartTipTypeValues.Percent) {
      return undefined;
    }

    const calculatedAmount = this.calculateAmount(choiceAmount, this.totalAmount);
    return calculatedAmount > 0 ? calculatedAmount : undefined;
  }

  isChoiceActive(choiceAmount: number, choiceUnit: string): boolean {
    if (choiceUnit === CartTipTypeValues.Currency) {
      return this.selectedTip === choiceAmount;
    }

    const calculatedAmount = this.calculateAmount(choiceAmount, this.totalAmount);
    return calculatedAmount > 0 && this.selectedTip === calculatedAmount;
  }

  checkOtherAmountIsSelected(): void {
    this.isChoice1Selected = this.isChoiceActive(this.choice1Amount, this.choice1Unit);
    this.isChoice2Selected = this.isChoiceActive(this.choice2Amount, this.choice2Unit);
    this.isChoice3Selected = this.isChoiceActive(this.choice3Amount, this.choice3Unit);
    this.isOtherAmountSelected =
      !!this.selectedTip &&
      !this.isChoice1Selected &&
      !this.isChoice2Selected &&
      !this.isChoice3Selected;
    this.otherAmount =
      !!this.selectedTip &&
      !this.isChoice1Selected &&
      !this.isChoice2Selected &&
      !this.isChoice3Selected
        ? this.selectedTip
        : 0;
  }
}
